import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import type { DragEvent, MouseEvent } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import DraggableBidLadderStackedBar from '@/features/bidding/components/cascading/bid-ladder/drag-and-drop/DraggableBidLadderStackedBar'
import BidLadderForecastChip from '@/features/bidding/components/cascading/bid-ladder/shared/BidLadderForecastChip'
import BidLadderPriceInput from '@/features/bidding/components/cascading/bid-ladder/shared/BidLadderPriceInput'
import BidLadderTotalMW from '@/features/bidding/components/cascading/bid-ladder/shared/BidLadderTotalMW'
import VolumeBidLadderStackedBar from '@/features/bidding/components/cascading/bid-ladder/volume-allocation/VolumeBidLadderStackedBar'
import type {
  BidDirection,
  BidLevelActivationGroup,
  BidLevelInput,
  DragPayload,
  VolumeCapacityConfig,
} from '@/features/bidding/types/bidLadderInput'
import type { LevelPricingConfig } from '@/features/bidding/types/forecastPricing'

type BidLadderInputRowProps = {
  level: BidLevelInput
  /** Display number for this level (1 = bottom, 6 = top) */
  levelNumber: number
  /** Total MW across ALL levels for BOTH directions (for relative bar width comparison) */
  totalMWAcrossAll: number
  /** Total MW for UP direction in THIS level only */
  upTotalMW?: number
  /** Total MW for DOWN direction in THIS level only */
  downTotalMW?: number
  /** Whether to show the MW column (defaults to true) */
  showTotalMW?: boolean
  /** Groups to preview when dragging over this level */
  previewGroups?: BidLevelActivationGroup[]
  /** Whether the UP price has a validation error */
  upPriceError?: boolean
  /** Whether the DOWN price has a validation error */
  downPriceError?: boolean
  /** Key of group that should receive focus after keyboard move (format: levelId-direction-regulationObjectCode) */
  focusedGroupKey?: string | null
  /** Pricing configuration for this level (from configuration tab) */
  pricingConfig?: LevelPricingConfig
  /** Whether this level's pricing is currently focused in the configuration tab */
  isPricingFocused?: boolean
  /** Called when the forecast chip is clicked to focus the level in configuration tab */
  onPricingFocus?: () => void
  onPriceChange: (levelId: string, direction: BidDirection, price: number | null) => void
  onDrop: (levelId: string, payload: DragPayload) => void
  onContextMenu: (event: MouseEvent, group: BidLevelActivationGroup, levelId: string) => void
  onDragStartFromBar?: (payload: DragPayload) => void
  onDragEndFromBar?: () => void
  onDragOverRow?: (levelId: string) => void
  onDragLeaveRow?: () => void
  onKeyboardMove?: (levelId: string, group: BidLevelActivationGroup, moveDirection: 'up' | 'down') => void
  onClearFocusedGroupKey?: () => void
  /** Callback when volume capacity configuration changes */
  onVolumeCapacityChange?: (levelId: string, direction: BidDirection, config: VolumeCapacityConfig) => void
  /** Number of decimal places allowed when mode is 'fixed' */
  fixedModeDecimalPlaces?: number
}

function BidLadderInputRow({
  level,
  levelNumber,
  totalMWAcrossAll,
  upTotalMW,
  downTotalMW,
  showTotalMW = true,
  previewGroups,
  upPriceError,
  downPriceError,
  focusedGroupKey,
  pricingConfig,
  isPricingFocused,
  onPricingFocus,
  onPriceChange,
  onDrop,
  onContextMenu,
  onDragStartFromBar,
  onDragEndFromBar,
  onDragOverRow,
  onDragLeaveRow,
  onKeyboardMove,
  onClearFocusedGroupKey,
  onVolumeCapacityChange,
  fixedModeDecimalPlaces,
}: Readonly<BidLadderInputRowProps>) {
  const { t } = useTranslation()
  const [isDragOver, setIsDragOver] = useState(false)
  const [hoveredRegulationObjectCode, setHoveredRegulationObjectCode] = useState<string | null>(null)

  // Derive if drop handlers should be active from whether drag handlers are provided
  const isDropEnabled = Boolean(onDragStartFromBar)

  const handleDragOver = useCallback(
    (event: DragEvent) => {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
      if (!isDragOver) {
        setIsDragOver(true)
        onDragOverRow?.(level.id)
      }
    },
    [isDragOver, level.id, onDragOverRow],
  )

  const handleDragEnter = useCallback(
    (event: DragEvent) => {
      event.preventDefault()
      setIsDragOver(true)
      onDragOverRow?.(level.id)
    },
    [level.id, onDragOverRow],
  )

  const handleDragLeave = useCallback(
    (event: DragEvent) => {
      // Only reset if leaving the row entirely (not entering a child)
      if (!event.currentTarget.contains(event.relatedTarget as Node)) {
        setIsDragOver(false)
        onDragLeaveRow?.()
      }
    },
    [onDragLeaveRow],
  )

  const handleDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault()
      setIsDragOver(false)

      try {
        const data = event.dataTransfer.getData('application/json')
        const payload = JSON.parse(data) as DragPayload
        onDrop(level.id, payload)
      } catch (error) {
        console.error('Failed to parse drag data:', error)
      }
    },
    [level.id, onDrop],
  )

  const handleContextMenu = useCallback(
    (event: MouseEvent, group: BidLevelActivationGroup) => {
      event.preventDefault()
      onContextMenu(event, group, level.id)
    },
    [level.id, onContextMenu],
  )

  // Memoize preview MW calculations
  const previewUpMW = useMemo(
    () => (previewGroups ?? []).filter((g) => g.direction === 'up').reduce((sum, g) => sum + g.capacityMW, 0),
    [previewGroups],
  )
  const previewDownMW = useMemo(
    () => (previewGroups ?? []).filter((g) => g.direction === 'down').reduce((sum, g) => sum + g.capacityMW, 0),
    [previewGroups],
  )

  const handleGroupHover = useCallback((regulationObjectCode: string | null) => {
    setHoveredRegulationObjectCode(regulationObjectCode)
  }, [])

  const handleKeyboardMove = useCallback(
    (group: BidLevelActivationGroup, moveDirection: 'up' | 'down') => {
      onKeyboardMove?.(level.id, group, moveDirection)
    },
    [level.id, onKeyboardMove],
  )

  const handleDownVolumeCapacityChange = useCallback(
    (config: VolumeCapacityConfig) => {
      onVolumeCapacityChange?.(level.id, 'down', config)
    },
    [level.id, onVolumeCapacityChange],
  )

  const handleUpVolumeCapacityChange = useCallback(
    (config: VolumeCapacityConfig) => {
      onVolumeCapacityChange?.(level.id, 'up', config)
    },
    [level.id, onVolumeCapacityChange],
  )

  const dividerSx = {
    width: '1px',
    alignSelf: 'stretch',
    backgroundColor: 'divider',
    my: -0.75, // Extend beyond row padding to connect with adjacent rows
  }

  return (
    <Box
      aria-label={t('bidding.bidding_cascading.bid_ladder.row_aria_label', { level: levelNumber })}
      role="listitem"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.5,
        py: 0.75,
        backgroundColor: isDragOver ? 'action.hover' : 'transparent',
        outline: isDragOver ? '2px solid' : 'none',
        outlineColor: 'primary.main',
        outlineOffset: '-2px',
        transition: 'background-color 0.12s ease, outline 0.12s ease',
        minHeight: { xs: 44, sm: 40 },
        '@media (hover: hover)': {
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        },
      }}
      onDragEnter={isDropEnabled ? handleDragEnter : undefined}
      onDragLeave={isDropEnabled ? handleDragLeave : undefined}
      onDragOver={isDropEnabled ? handleDragOver : undefined}
      onDrop={isDropEnabled ? handleDrop : undefined}
    >
      {/* Level Number Badge */}
      <Box
        sx={{
          width: 24,
          height: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'action.hover',
          borderRadius: '4px',
        }}
      >
        <Typography
          sx={{
            fontSize: '0.8125rem',
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            color: 'text.primary',
          }}
          variant="caption"
        >
          {levelNumber}
        </Typography>
      </Box>

      <Box sx={dividerSx} />

      {/* DOWN Total MW */}
      {showTotalMW && (
        <>
          <BidLadderTotalMW direction="down" previewMW={previewDownMW} totalMW={downTotalMW ?? 0} />
          <Box sx={dividerSx} />
        </>
      )}

      {/* DOWN Bar Chart */}
      {isDropEnabled && onDragStartFromBar && onDragEndFromBar ? (
        <DraggableBidLadderStackedBar
          direction="down"
          focusedGroupKey={focusedGroupKey}
          groups={level.assignedGroups}
          hoveredRegulationObjectCode={hoveredRegulationObjectCode}
          levelId={level.id}
          previewGroups={previewGroups}
          totalMWAcrossAll={totalMWAcrossAll}
          onClearFocusedGroupKey={onClearFocusedGroupKey}
          onContextMenu={handleContextMenu}
          onDragEndFromBar={onDragEndFromBar}
          onDragStartFromBar={onDragStartFromBar}
          onGroupHover={handleGroupHover}
          onKeyboardMove={handleKeyboardMove}
        />
      ) : (
        <VolumeBidLadderStackedBar
          direction="down"
          fixedModeDecimalPlaces={fixedModeDecimalPlaces}
          focusedGroupKey={focusedGroupKey}
          groups={level.assignedGroups}
          hoveredRegulationObjectCode={hoveredRegulationObjectCode}
          levelId={level.id}
          totalMWAcrossAll={totalMWAcrossAll}
          volumeCapacityConfig={level.downVolumeCapacity!}
          onClearFocusedGroupKey={onClearFocusedGroupKey}
          onContextMenu={handleContextMenu}
          onGroupHover={handleGroupHover}
          onVolumeCapacityChange={handleDownVolumeCapacityChange}
        />
      )}

      <Box sx={dividerSx} />

      {/* DOWN Price Input or Forecast Chip */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
        }}
      >
        {onPricingFocus && (
          <Tooltip placement="left" title={t('bidding.bidding_cascading.bid_ladder.configure_pricing')}>
            <IconButton
              aria-label={t('bidding.bidding_cascading.bid_ladder.configure_pricing')}
              size="small"
              sx={{
                padding: 0.25,
                color: 'action.active',
                '& .MuiSvgIcon-root': {
                  fontSize: '1rem',
                },
                '&:hover': {
                  color: 'primary.main',
                },
              }}
              onClick={onPricingFocus}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        )}
        {pricingConfig?.mode === 'forecast' ? (
          <BidLadderForecastChip
            direction="down"
            isFocused={isPricingFocused}
            market={pricingConfig.market}
            quantile={pricingConfig.quantile}
            onClick={onPricingFocus}
          />
        ) : (
          <BidLadderPriceInput
            direction="down"
            error={downPriceError}
            value={level.downPrice}
            onChange={(price) => onPriceChange(level.id, 'down', price)}
          />
        )}
      </Box>

      {/* Center Vertical Divider */}
      <Box sx={dividerSx} />

      {/* UP Price Input or Forecast Chip */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
        }}
      >
        {pricingConfig?.mode === 'forecast' ? (
          <BidLadderForecastChip
            direction="up"
            isFocused={isPricingFocused}
            market={pricingConfig.market}
            quantile={pricingConfig.quantile}
            onClick={onPricingFocus}
          />
        ) : (
          <BidLadderPriceInput
            direction="up"
            error={upPriceError}
            value={level.upPrice}
            onChange={(price) => onPriceChange(level.id, 'up', price)}
          />
        )}
        {onPricingFocus && (
          <Tooltip placement="right" title={t('bidding.bidding_cascading.bid_ladder.configure_pricing')}>
            <IconButton
              aria-label={t('bidding.bidding_cascading.bid_ladder.configure_pricing')}
              size="small"
              sx={{
                padding: 0.25,
                color: 'action.active',
                '& .MuiSvgIcon-root': {
                  fontSize: '1rem',
                },
                '&:hover': {
                  color: 'primary.main',
                },
              }}
              onClick={onPricingFocus}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={dividerSx} />

      {/* UP Bar Chart */}
      {isDropEnabled && onDragStartFromBar && onDragEndFromBar ? (
        <DraggableBidLadderStackedBar
          direction="up"
          focusedGroupKey={focusedGroupKey}
          groups={level.assignedGroups}
          hoveredRegulationObjectCode={hoveredRegulationObjectCode}
          levelId={level.id}
          previewGroups={previewGroups}
          totalMWAcrossAll={totalMWAcrossAll}
          onClearFocusedGroupKey={onClearFocusedGroupKey}
          onContextMenu={handleContextMenu}
          onDragEndFromBar={onDragEndFromBar}
          onDragStartFromBar={onDragStartFromBar}
          onGroupHover={handleGroupHover}
          onKeyboardMove={handleKeyboardMove}
        />
      ) : (
        <VolumeBidLadderStackedBar
          direction="up"
          fixedModeDecimalPlaces={fixedModeDecimalPlaces}
          focusedGroupKey={focusedGroupKey}
          groups={level.assignedGroups}
          hoveredRegulationObjectCode={hoveredRegulationObjectCode}
          levelId={level.id}
          totalMWAcrossAll={totalMWAcrossAll}
          volumeCapacityConfig={level.upVolumeCapacity!}
          onClearFocusedGroupKey={onClearFocusedGroupKey}
          onContextMenu={handleContextMenu}
          onGroupHover={handleGroupHover}
          onVolumeCapacityChange={handleUpVolumeCapacityChange}
        />
      )}

      {/* UP Total MW */}
      {showTotalMW && (
        <>
          <Box sx={dividerSx} />
          <BidLadderTotalMW direction="up" previewMW={previewUpMW} totalMW={upTotalMW ?? 0} />
        </>
      )}
    </Box>
  )
}

export default BidLadderInputRow
